import { wordsToSentences, sentencesToParagraphs } from "../V2TextEditor/utils";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { isScriptClip, ScriptLayerClip } from "../../../engine/clips";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import type { V2TranscriptWord } from "../V2TextEditor";

export type ChaptersTranscriptBlock = {
    id: string;
    startSec: number;
    text: string;
    afterWordPayload: any;
    beforeWordPayload?: any;
    boundaryTimeSec: number;
};

type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;

const toFiniteNumber = (value: any, fallback: number): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const joinWordsAsText = (words: Array<{ text: string }>): string => {
    let out = "";
    const noSpaceBefore = (t: string) => /^[.,!?;:)\]]$/.test(t);
    for (const w of words) {
        const t = String(w?.text ?? "");
        if (!t) continue;
        if (out && !noSpaceBefore(t)) out += " ";
        out += t;
    }
    return out.trim();
};

export async function buildEditedTranscriptBlocksForChapters(args: {
    getTranscript: GetTranscriptFn;
    projectId: string;
    scriptClipId?: string | null;
}): Promise<{ blocks: ChaptersTranscriptBlock[]; totalWords: number }> {
    const projectId = String(args.projectId ?? "");
    if (!projectId) return { blocks: [], totalWords: 0 };

    const scriptClipId =
        String(args.scriptClipId ?? "") ||
        String((CompositionService.composition as any)?.scriptLayer?.clipId ?? "");
    if (!scriptClipId) return { blocks: [], totalWords: 0 };

    const clip = CompositionService.getClipById(scriptClipId);
    if (!clip || !isScriptClip(clip)) return { blocks: [], totalWords: 0 };

    const scriptClip = clip as ScriptLayerClip;
    const audioEntries = scriptClip.getAudioEntries();
    const getTranscript = args.getTranscript;

    const scriptSegments: Array<{ srcStartSec: number; srcEndSec: number; isGap?: boolean }> =
        Array.isArray((scriptClip as any).scriptSegments) && (scriptClip as any).scriptSegments.length > 0
            ? ((scriptClip as any).scriptSegments as any[])
            : [];

    const isWordInPlayableSection = (clipStart: number, clipEnd: number): boolean => {
        if (!scriptSegments.length) return true;
        for (const seg of scriptSegments) {
            if ((seg as any).isGap) continue;
            if (clipStart >= Number(seg.srcStartSec ?? 0) && clipEnd <= Number(seg.srcEndSec ?? 0)) {
                return true;
            }
        }
        return false;
    };

    const transcriptHiddenRanges: Array<{ startSec: number; endSec: number }> = Array.isArray(
        (scriptClip as any).transcriptHiddenRanges,
    )
        ? ((scriptClip as any).transcriptHiddenRanges as any[])
        : [];
    const isHiddenAt = (clipTime: number): boolean => {
        if (!transcriptHiddenRanges.length) return false;
        for (const r of transcriptHiddenRanges) {
            const start = Number((r as any)?.startSec ?? 0);
            const end = Number((r as any)?.endSec ?? 0);
            if (clipTime >= start && clipTime < end) return true;
        }
        return false;
    };

    const words: Array<V2TranscriptWord & { sourceIndex: number; srcStartSec?: number; srcEndSec?: number }> = [];

    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
        const entry: any = audioEntries[sourceIndex];
        const entryGain = Number(entry?.gain ?? 1);
        if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;

        const assetId = String(entry?.assetId ?? "");
        if (!assetId) continue;

        const trackRecord = ProjectAssetsStore.projectTracks.find(
            (t: any) => t?.id === assetId || t?.trackId === assetId,
        );
        const transcriptId = String(trackRecord?.activeTranscriptId ?? "");
        if (!transcriptId) continue;

        const transcriptJson = await getTranscript({ transcriptId, projectId });
        if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) continue;

        const sourceDuration = Number(trackRecord?.duration ?? 0) || 0;

        const entrySegments: Array<{
            offsetSec: number;
            playbackRate: number;
            rangeInSec: number;
            rangeOutSec: number;
        }> = [];

        const segmentsRaw: any[] = Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : [];
        if (segmentsRaw.length > 0) {
            for (const seg of segmentsRaw) {
                const segOffset = toFiniteNumber(seg?.offsetSec, 0);
                const segRate = toFiniteNumber(seg?.playbackRate ?? entry?.playbackRate, 1) || 1;
                const segIn = toFiniteNumber(seg?.range?.inSec, 0);
                const segOut = toFiniteNumber(seg?.range?.outSec, 0);
                if (!Number.isFinite(segOffset) || !Number.isFinite(segRate) || segRate <= 0) continue;
                if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
                entrySegments.push({
                    offsetSec: segOffset,
                    playbackRate: segRate,
                    rangeInSec: segIn,
                    rangeOutSec: segOut,
                });
            }
        } else {
            const rangeInSec = toFiniteNumber(entry?.range?.inSec, 0);
            const rangeOutSec = toFiniteNumber(entry?.range?.outSec, sourceDuration > 0 ? sourceDuration : Infinity);
            const offsetSec = toFiniteNumber(entry?.offsetSec, 0);
            const playbackRate = toFiniteNumber(entry?.playbackRate, 1) || 1;
            entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
        }

        for (const w of transcriptJson.words) {
            if ((w?.type ?? "word") !== "word") continue;

            const wordStartSrc = toFiniteNumber(w?.t0 ?? w?.start, 0);
            const wordEndSrc = toFiniteNumber(w?.t1 ?? w?.end, wordStartSrc);
            if (!Number.isFinite(wordStartSrc) || !Number.isFinite(wordEndSrc) || wordEndSrc < wordStartSrc) continue;

            const rawText = String(w?.text ?? w?.word ?? "").trim();
            if (!rawText) continue;

            // Map this source-time word into the script clip local time using the first matching segment.
            let mapped: { startSec: number; endSec: number; srcStartSec: number; srcEndSec: number } | null = null;
            for (const seg of entrySegments) {
                const EPS = 1e-3;
                if (wordStartSrc < seg.rangeInSec - EPS) continue;
                if (wordEndSrc > seg.rangeOutSec + EPS) continue;
                const clipStart = seg.offsetSec + (wordStartSrc - seg.rangeInSec) / seg.playbackRate;
                const clipEnd = seg.offsetSec + (wordEndSrc - seg.rangeInSec) / seg.playbackRate;
                mapped = { startSec: clipStart, endSec: clipEnd, srcStartSec: wordStartSrc, srcEndSec: wordEndSrc };
                break;
            }
            if (!mapped) continue;

            if (!isWordInPlayableSection(mapped.startSec, mapped.endSec)) continue;
            if (isHiddenAt(mapped.startSec)) continue;

            const corrected =
                ScriptLayerStore.getWordCorrectionText({
                    sourceIndex,
                    srcStartSec: mapped.srcStartSec,
                    srcEndSec: mapped.srcEndSec,
                }) ?? null;

            const text = corrected ? corrected : rawText;

            words.push({
                text,
                startSec: mapped.startSec,
                endSec: mapped.endSec,
                sourceIndex,
                srcStartSec: mapped.srcStartSec,
                srcEndSec: mapped.srcEndSec,
            });
        }
    }

    words.sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));

    if (!words.length) return { blocks: [], totalWords: 0 };

    const sentences = wordsToSentences(words);
    const paragraphs = sentencesToParagraphs(sentences);

    const blocks: ChaptersTranscriptBlock[] = [];
    let prevLastWord: any | undefined = undefined;

    for (const para of paragraphs) {
        const paraWords: any[] = [];
        for (const s of para.sentences) {
            paraWords.push(...(s?.words ?? []));
        }
        if (!paraWords.length) continue;

        const firstWord = paraWords[0];
        const lastWord = paraWords[paraWords.length - 1];
        if (!firstWord) continue;

        const id = `b${blocks.length}`;
        const startSec = Number(firstWord?.startSec ?? 0);
        const text = joinWordsAsText(paraWords);

        const boundaryTimeSec =
            prevLastWord && Number.isFinite(prevLastWord?.endSec)
                ? Number(prevLastWord.endSec)
                : prevLastWord && Number.isFinite(prevLastWord?.startSec)
                  ? Number(prevLastWord.startSec)
                  : 0;

        blocks.push({
            id,
            startSec: Number.isFinite(startSec) ? startSec : 0,
            text,
            afterWordPayload: firstWord,
            beforeWordPayload: prevLastWord,
            boundaryTimeSec,
        });

        prevLastWord = lastWord;
    }

    return { blocks, totalWords: words.length };
}

